import { Telemetry, ValidTelemetryEvent } from '~commonInfra/services/Telemetry';
import { IContentAndLuidInfo } from '~src/catalog/models/mappers/DatabaseAndTableMapper';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';

/**
 * PFT (Product Feature Telemetry) event fired when a user saves a tag change
 * (add and/or remove) on an external catalog asset -- database, table,
 * virtual connection, or virtual connection table. Tableau-owned content
 * (workbook, view, datasource, flow) is intentionally excluded; its tag
 * changes flow through the same `createExecuteFn` helper but are out of
 * scope for the EAD PFT feature.
 *
 * Column-level tag changes (single-column inline editor) emit the same event
 * from catalog-ui with `content_type: 'column'`. The wire shape and Juno
 * schema are shared across both emit sides.
 *
 * Mapped in Juno to:
 *   Tableau Cloud > Catalog > External Asset Details (Cloud) > Modify Tags
 *
 * Wire event name: `tag_modified`
 * Action verb:     `completed` (added downstream by Juno-registered schema)
 * Org ID type:     `tenant_id`
 *
 * Granularity decision (PM-confirmed): one event per Save click rather than
 * one event per tag delta. A multi-tag save (e.g., add 3 tags + remove 1)
 * fires one event with `tags_added_count: 3, tags_removed_count: 1`.
 * Avoids burst-emission on bulk operations and keeps the dashboard math
 * straightforward.
 *
 * Bulk-selection note: when the user applies the same tag change to N
 * assets in one Save (multi-select), one event fires per content-type slice
 * with `affected_count: N`. Mixed-content saves (e.g., 2 databases + 3
 * tables) fan out via `changeTagsForContent` and produce one event per
 * content type, each with its own `affected_count`.
 *
 * Emitted after the change-tags REST call resolves successfully (for the
 * PublishedConnection / PublishedConnectionTable sites that return a soft
 * IResult, "successful" means the resolved result carries no errors; for
 * the Database / Table sites that use .then/.catch, "successful" is the
 * .then branch). Matches catalog-ui's flipped post-review behavior so
 * both halves of the shared Juno schema mean "successful save".
 *
 * PII discipline: we do NOT log raw tag strings (user-defined free text).
 * Only counts and categorical descriptors.
 */
export const TAG_MODIFIED_EVENT_TYPE = 'tag_modified';

export interface TagModifiedTelemetryEvent extends ValidTelemetryEvent {
  /** Wire event name. Always `tag_modified`. */
  type: typeof TAG_MODIFIED_EVENT_TYPE;
  /**
   * PFT (Product Feature Telemetry) Standard Attribute for tenant routing. On Tableau Cloud
   * the tenant is the site, so this equals `site_luid`. Set per-event on the EAD telemetry
   * helpers rather than on `Telemetry.getCommonProperties()` so non-EAD vpc events (owned by
   * other teams) don't inherit a Standard Attribute their schemas don't declare.
   */
  tenant_id: string;
  /** Catalog content type the tag change is being applied to. */
  content_type: string;
  /**
   * Connector backend (snowflake / databricks / postgres / ...) of the first affected asset.
   * Empty string when unavailable.
   *
   * Sources of emptiness, in decreasing order of expected frequency:
   *  1. virtualConnection / virtualConnectionTable on a REST response that doesn't yet
   *     carry the upstream connector backend. The PublishedConnection mappers thread
   *     `catalogConnectionType` from the REST `connectionType` field; until the monolith
   *     server-side change ships, this field is undefined for VC/VCT and the emit
   *     falls back to empty string (W-22931576 / follow-up monolith WI).
   *  2. Multi-select with mixed connectors: only items[0]'s connector is reported for the
   *     whole affected_count (matches the catalog-ui schema which also has a single field).
   *  3. Tableau-owned content (workbook / datasource / flow) is out of scope for EAD so
   *     this helper isn't called from those code paths -- but if it were, `connection_type`
   *     would be empty by design.
   */
  connection_type: string;
  /** Number of tags added in this save. Zero if the save only removed tags. */
  tags_added_count: number;
  /** Number of tags removed in this save. Zero if the save only added tags. */
  tags_removed_count: number;
  /** Number of catalog assets affected by this save (>=1; >1 indicates a multi-select bulk action). */
  affected_count: number;
}

/**
 * Emit `tag_modified` for an EAD tag-change save. Caller passes the content
 * type (which they know based on which `changeTagsFor*` helper they live in)
 * and the affected items (so we can read `catalogConnectionType` from the
 * first one -- named distinctly from `IDataConnectionInfoProperties.connectionType`
 * to avoid an interface extends-clash; see IContentInfo for the rationale).
 *
 * No-op when `tagsToAdd` and `tagsToRemove` are both empty -- the helper is
 * resilient to spurious calls; callers don't have to pre-filter.
 */
export function sendTagModifiedTelemetry(params: {
  readonly contentType: string;
  readonly items: ReadonlyArray<IContentInfo | IContentAndLuidInfo>;
  readonly tagsToAdd: ReadonlyArray<string>;
  readonly tagsToRemove: ReadonlyArray<string>;
}): void {
  const { contentType, items, tagsToAdd, tagsToRemove } = params;

  if (tagsToAdd.length === 0 && tagsToRemove.length === 0) {
    return;
  }

  if (items.length === 0) {
    return;
  }

  Telemetry.sendEvent<TagModifiedTelemetryEvent>({
    type: TAG_MODIFIED_EVENT_TYPE,
    tenant_id: getSessionManagementServiceInstance().sessionSiteLuid(),
    content_type: contentType,
    // Multi-select with mixed connectors: we report the first item's connector for the whole
    // affected_count (matches catalog-ui's single-field schema). If per-connector slicing on
    // bulk becomes important, splitting the emit by connector at the call site is the path.
    connection_type: items[0]?.catalogConnectionType ?? '',
    tags_added_count: tagsToAdd.length,
    tags_removed_count: tagsToRemove.length,
    affected_count: items.length,
  });
}
